import { useState } from "react";
import { ActionButton } from "../../shared/common/Button";
import { TextMetricsBar } from "../../shared/components";
import {
  EDIT_INTENSITY_OPTIONS,
  LARGE_SELECTION_WARNING_THRESHOLD,
  QUICK_ACTIONS,
  TONE_OPTIONS,
} from "../constants";
import type { DocumentMetadata, EditIntensity } from "../types";
import type { CustomPreset } from "../useCustomPresets";

interface EditFormPanelProps {
  selectedText: string;
  wordCount: number;
  isLoading: boolean;
  prompt: string;
  selectedTone: string;
  editIntensity: EditIntensity;
  customPresets: CustomPreset[];
  isSavingPreset: boolean;
  presetName: string;
  streamingText: string;
  onQuickAction: (actionPrompt: string) => void;
  onToneChange: (tone: string) => void;
  onEditIntensityChange: (intensity: EditIntensity) => void;
  onPromptChange: (prompt: string) => void;
  onStartSavePreset: () => void;
  onPresetNameChange: (presetName: string) => void;
  onSubmitSavePreset: () => void;
  onCancelSavePreset: () => void;
  onDeletePreset: (presetId: string) => void;
  metadata: DocumentMetadata;
  onMetadataChange: (m: DocumentMetadata) => void;
  onGenerate: () => void;
  onAbort: () => void;
}

const EditFormPanel = ({
  selectedText,
  wordCount,
  isLoading,
  prompt,
  selectedTone,
  editIntensity,
  customPresets,
  isSavingPreset,
  presetName,
  streamingText,
  onQuickAction,
  onToneChange,
  onEditIntensityChange,
  onPromptChange,
  onStartSavePreset,
  onPresetNameChange,
  onSubmitSavePreset,
  onCancelSavePreset,
  onDeletePreset,
  metadata,
  onMetadataChange,
  onGenerate,
  onAbort,
}: EditFormPanelProps) => {
  const [isDocInfoOpen, setIsDocInfoOpen] = useState(false);

  const selectClass =
    "w-full px-2.5 py-2 border border-gray-200 rounded-lg text-xs bg-white text-gray-800 focus:outline-none focus:border-brand-500 focus:shadow-[0_0_0_3px_rgba(59,130,246,0.12)] disabled:opacity-50 disabled:cursor-not-allowed transition-all select-custom";

  return (
    <div className="space-y-4">

      {/* Selected text preview */}
      {selectedText && (
        <div className="bg-gray-50 border border-gray-100 rounded-xl p-3">
          <h2 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">
            Selected text
          </h2>
          <p className="text-xs text-gray-700 line-clamp-3 leading-relaxed">
            {selectedText.length > 200
              ? `${selectedText.substring(0, 200)}…`
              : selectedText}
          </p>
          <TextMetricsBar text={selectedText} />
        </div>
      )}

      {/* Large-selection warning */}
      {wordCount > LARGE_SELECTION_WARNING_THRESHOLD && (
        <div className="flex items-start gap-2.5 bg-amber-50 border border-amber-200 rounded-xl p-3">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="2" strokeLinecap="round" className="mt-0.5 flex-shrink-0">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
          <p className="text-xs text-amber-800 leading-relaxed">
            <strong>Large selection ({wordCount} words).</strong>{" "}
            Consider selecting a smaller passage for best results.
          </p>
        </div>
      )}

      {/* Document Info (collapsed by default) */}
      <div className="border border-gray-100 rounded-xl overflow-hidden">
        <button
          type="button"
          onClick={() => setIsDocInfoOpen((v) => !v)}
          aria-expanded={isDocInfoOpen}
          className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Document Info</span>
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ transform: isDocInfoOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.15s" }}
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
        {isDocInfoOpen && (
          <div className="px-3 py-2.5 space-y-2 bg-white">
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">
              Matter / Ref No.
              <input
                type="text"
                value={metadata.matterRef}
                maxLength={100}
                onChange={(e) => onMetadataChange({ ...metadata, matterRef: e.target.value })}
                placeholder="e.g. 2024-MAT-0042"
                className="mt-1 w-full px-2.5 py-1.5 border border-gray-200 rounded-lg text-xs text-gray-800 bg-white focus:outline-none focus:border-brand-500 transition-all normal-case font-normal"
              />
            </label>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">
              Document Description
              <input
                type="text"
                value={metadata.docDescription}
                maxLength={200}
                onChange={(e) => onMetadataChange({ ...metadata, docDescription: e.target.value })}
                placeholder="Brief description"
                className="mt-1 w-full px-2.5 py-1.5 border border-gray-200 rounded-lg text-xs text-gray-800 bg-white focus:outline-none focus:border-brand-500 transition-all normal-case font-normal"
              />
            </label>
          </div>
        )}
      </div>

      {/* Controls row */}
      <div className="grid grid-cols-3 gap-2">
        <div>
          <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">
            Quick Action
          </label>
          <select
            value=""
            onChange={(e) => { if (e.target.value) onQuickAction(e.target.value); }}
            disabled={isLoading}
            title="Select a quick action"
            className={selectClass}
          >
            <option value="" disabled>Select…</option>
            {QUICK_ACTIONS.map((action) => (
              <option key={action.label} value={action.prompt} title={action.prompt}>
                {action.label}
              </option>
            ))}
            {customPresets.length > 0 && (
              <optgroup label="My Presets">
                {customPresets.map((preset) => (
                  <option key={preset.id} value={preset.prompt} title={preset.prompt}>
                    ★ {preset.name}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
        </div>

        <div>
          <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">
            Tone
          </label>
          <select
            value={selectedTone}
            onChange={(e) => onToneChange(e.target.value)}
            disabled={isLoading}
            title="Select tone"
            className={selectClass}
          >
            <option value="">Default</option>
            {TONE_OPTIONS.map((tone) => (
              <option key={tone.id} value={tone.id} title={tone.description}>
                {tone.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">
            Intensity
          </label>
          <select
            value={editIntensity}
            onChange={(e) => onEditIntensityChange(e.target.value as EditIntensity)}
            disabled={isLoading}
            title="Select edit intensity"
            className={selectClass}
          >
            {EDIT_INTENSITY_OPTIONS.map((opt) => (
              <option key={opt.id} value={opt.id} title={opt.description}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Instructions */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label
            htmlFor="ai-edit-prompt"
            className="text-[10px] font-bold text-gray-400 uppercase tracking-wider"
          >
            Instructions
          </label>

          {isSavingPreset ? (
            <div className="flex items-center gap-1">
              <input
                type="text"
                value={presetName}
                onChange={(e) => onPresetNameChange(e.target.value)}
                placeholder="Preset name"
                className="text-xs px-2 py-1 border border-gray-200 rounded-lg w-24 focus:outline-none focus:border-brand-500 focus:shadow-[0_0_0_3px_rgba(59,130,246,0.12)] transition-all"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); onSubmitSavePreset(); }
                  if (e.key === "Escape") onCancelSavePreset();
                }}
              />
              <button
                onClick={onSubmitSavePreset}
                style={{ background: "linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)" }}
                className="text-[10px] text-white px-2 py-1 rounded-lg font-semibold"
              >
                Save
              </button>
              <button
                onClick={onCancelSavePreset}
                className="text-[10px] text-gray-400 hover:text-gray-600 px-1 transition-colors"
              >
                Cancel
              </button>
            </div>
          ) : (
            prompt.trim() && (
              <button
                onClick={onStartSavePreset}
                title="Save as preset"
                className="flex items-center gap-1 text-[10px] text-gray-400 hover:text-brand-500 transition-colors"
              >
                <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                Save preset
              </button>
            )
          )}
        </div>

        <textarea
          id="ai-edit-prompt"
          value={prompt}
          onChange={(e) => onPromptChange(e.target.value)}
          placeholder="Describe your edits… (Ctrl+Enter to submit)"
          rows={3}
          aria-label="AI edit instructions"
          className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-800 placeholder-gray-400 bg-white focus:outline-none focus:border-brand-500 focus:shadow-[0_0_0_3px_rgba(59,130,246,0.12)] transition-all resize-none leading-relaxed"
        />

        {/* Custom preset chips */}
        {customPresets.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {customPresets.map((preset) => (
              <span
                key={preset.id}
                className="inline-flex items-center gap-1 text-[10px] bg-brand-50 text-brand-600 border border-brand-100 rounded-full px-2 py-0.5 font-medium"
              >
                <button
                  onClick={() => onQuickAction(preset.prompt)}
                  title={preset.prompt}
                  className="hover:underline"
                >
                  {preset.name}
                </button>
                <button
                  onClick={() => onDeletePreset(preset.id)}
                  title="Delete preset"
                  className="text-brand-300 hover:text-red-500 transition-colors leading-none"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Generate / Stop button */}
      {isLoading ? (
        <button
          onClick={onAbort}
          aria-label="Stop generating AI suggestion"
          className="w-full flex items-center justify-center gap-2 bg-red-500 hover:bg-red-600 text-white text-sm font-semibold px-4 py-2.5 rounded-xl active:scale-[0.98] transition-all duration-150"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="1"/></svg>
          Stop generating
        </button>
      ) : (
        <ActionButton
          title="Get AI suggestion"
          loadingTitle="Improving…"
          onClick={onGenerate}
          isLoading={isLoading}
          disabled={isLoading}
        />
      )}

      {/* Streaming preview */}
      {streamingText && (
        <div
          className="bg-brand-50 border border-brand-100 rounded-xl p-3"
          role="status"
          aria-live="polite"
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="w-1.5 h-1.5 rounded-full bg-brand-500 animate-pulse-soft" />
            <span className="text-[10px] font-bold text-brand-500 uppercase tracking-wider">
              Generating…
            </span>
          </div>
          <p className="text-xs text-gray-700 whitespace-pre-wrap leading-relaxed max-h-[180px] overflow-y-auto">
            {streamingText}
          </p>
        </div>
      )}
    </div>
  );
};

export default EditFormPanel;
